import { useState, useEffect, useRef, useContext } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthContext, YearContext } from "../../context/context";
import { updateGoal, fetchCategories } from "../../api";
import BtnPrimaryL from "../Buttons/BtnPrimaryL";
import BtnSecondaryM from "../Buttons/BtnSecondaryM";
import BtnSecondaryS from "../Buttons/BtnSecondaryS";
import InputText from "../Inputs/InputText";
import InputDate from "../Inputs/InputDate";
import InputFile from "../Inputs/InputFile";
import InputCheckbox from "../Inputs/InputCheckbox";
import InputSelect from "../Inputs/InputSelect";
import IconCircle from "../Elements/IconCircle";
import { HiOutlineChevronDoubleRight } from "react-icons/hi";
import { IoCloseOutline } from "react-icons/io5";
import DropdownInput from "../Inputs/DropdownInput";
import Subheadline from "../Elements/Subheadline";
import Headline from "../Elements/Headline";
import { useToast } from "../../context/ToastContext";
import { FiPlus } from "react-icons/fi";
import { useLanguage } from "../../context/LanguageContext";
import PropTypes from "prop-types";

const formatDate = (date) => {
  if (!date) return "";
  if (typeof date === "string") {
    let parsedDate;
    // Check for dd/mm/yyyy format
    if (date.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = date.split("/").map(Number);
      parsedDate = new Date(year, month - 1, day); // Month is 0-indexed
    } else {
      // Assume YYYY-MM-DD or other parsable string
      parsedDate = new Date(date);
    }

    if (!isNaN(parsedDate)) {
      date = parsedDate;
    } else {
      return ""; // Invalid string date
    }
  }

  if (date instanceof Date) {
    // Return YYYY-MM-DD for input value using local time
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }
  return "";
};

const milestoneSchema = z
  .object({
    title: z.string().min(1, "Milestone title is required"),
    deadline: z.string().min(1, "Deadline is required"),
    repeat: z.boolean().optional(),
    repeatInterval: z.preprocess((val) => {
      if (typeof val !== "string") return undefined;
      return val === "" ? undefined : val;
    }, z.string().optional()),
    repeatUntil: z.string().optional(),
    status: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.repeat) {
        return !!data.repeatInterval;
      }
      return true;
    },
    {
      path: ["repeatInterval"],
      message: "Repeat interval is required when repeat is checked",
    }
  )
  .refine(
    (data) => {
      if (data.repeat && data.repeatInterval) {
        return !!data.repeatUntil;
      }
      return true;
    },
    {
      path: ["repeatUntil"],
      message:
        "End date is required when repeat is checked and interval is selected",
    }
  );

// how milestones are fetched? they do not have ids
const goalEditSchema = z.object({
  goal_title: z.string().min(1, "Goal title is required"),
  goal_img: z.any().optional(),
  goal_year: z.preprocess(
    (val) => {
      if (typeof val === "string" && val.trim() === "") return undefined;
      return Number(val);
    },
    z
      .number()
      .int("Year must be an integer")
      .gte(1000, "Enter a valid 4-digit year")
      .lte(9999, "Enter a valid 4-digit year")
  ),
  goal_category: z.string().min(1, "Category is required"),
  milestones: z
    .array(milestoneSchema)
    .min(1, "At least one milestone is required"),
});

// Simple hook to detect mobile (md breakpoint)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

export default function NavSidebarEdit({
  setSidebarEditToggled,
  selectedGoal,
}) {
  const { userData, setUserData } = useAuthContext();
  const { years: contextYears } = useContext(YearContext);
  const { showToast } = useToast();
  const { copy } = useLanguage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filteredYears, setFilteredYears] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [categoryDropdown, setCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef(null);
  const newMilestoneFlags = useRef([]);
  const isMobile = useIsMobile();
  const [expandedGroups, setExpandedGroups] = useState({});

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(goalEditSchema),
    defaultValues: {
      goal_title: selectedGoal?.title || "",
      goal_img: selectedGoal?.img || null,
      goal_year: selectedGoal?.year || "",
      goal_category: selectedGoal?.category || "",
      milestones: [],
    },
  });

  const {
    fields: milestones,
    insert,
    remove,
  } = useFieldArray({
    control,
    name: "milestones",
  });

  const watchedMilestones = watch("milestones") || [];

  useEffect(() => {
    if (!selectedGoal?.id || !userData.milestones) return;

    const goalMilestones = userData.milestones
      .filter((m) => m.goal_id === selectedGoal.id)
      .map((m) => ({
        ...m,
        deadline: m.deadline?.split("T")[0] || "",
        status: m.status ?? false,
      }));

    newMilestoneFlags.current = goalMilestones.map(() => false);

    reset({
      goal_title: selectedGoal?.title || "",
      goal_img: selectedGoal?.img || null,
      goal_year: selectedGoal?.year || "",
      goal_category: selectedGoal?.category || "",
      milestones: goalMilestones,
    });
  }, [selectedGoal?.id, userData.milestones, reset]);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        // Use years from context instead of fetching, ensuring 2025 and 2026 are always available
        const currentYear = new Date().getFullYear();
        const fallbackYears = [currentYear, currentYear + 1, currentYear + 2];
        const allYears = [...new Set([...contextYears, ...fallbackYears])];
        setFilteredYears(allYears);
        let categoriesData = await fetchCategories();
        // Deduplicate categories (case-insensitive)
        const seen = new Set();
        categoriesData = categoriesData.filter((cat) => {
          const lower = cat.toLowerCase();
          if (seen.has(lower)) return false;
          seen.add(lower);
          return true;
        });
        setCategories(categoriesData);
        setFilteredCategories(categoriesData);
      } catch (err) {
        console.error("Error loading dropdowns:", err);
      }
    };
    loadDropdowns();
  }, [contextYears]);

  useEffect(() => {
    const currentGoalYear = watch("goal_year");
    if (currentGoalYear) {
      // Only update milestones that have repeat enabled
      const updatedMilestones = watchedMilestones.map((milestone) => {
        if (
          milestone.repeat &&
          milestone.repeatUntil !==
            formatDate(new Date(currentGoalYear, 11, 31))
        ) {
          return {
            ...milestone,
            repeatUntil: formatDate(new Date(currentGoalYear, 11, 31)),
          };
        }
        return milestone;
      });

      // Only call setValue if there were actual changes
      if (
        JSON.stringify(updatedMilestones) !== JSON.stringify(watchedMilestones)
      ) {
        setValue("milestones", updatedMilestones);
      }
    }
  }, [watch("goal_year"), setValue, watchedMilestones]);

  const onSubmit = async (data) => {
    if (isSubmitting) return; // Prevent double submission
    setIsSubmitting(true);
    try {
      // console.log("Submitting form data:", data);

      // Process milestones to expand repeated ones
      const MAX_REPEATS = 366;

      // Batch process milestones
      const processedMilestones = data.milestones.reduce((acc, milestone) => {
        // For new milestones with repeat
        if (!milestone.id && milestone.repeat && milestone.repeatInterval) {
          let startDate = new Date(milestone.deadline);
          const originalDay = startDate.getDate();
          const repeatUntilDate = milestone.repeatUntil
            ? new Date(milestone.repeatUntil + "T23:59:59")
            : new Date(`${data.goal_year}-12-31T23:59:59`); // Use repeatUntil or end of year
          let repeatCount = 0;

          // Pre-calculate all dates for this milestone
          const dates = [];
          while (startDate <= repeatUntilDate && repeatCount < MAX_REPEATS) {
            dates.push(new Date(startDate));
            repeatCount++;

            // Calculate next date
            const nextDate = new Date(startDate);
            switch (milestone.repeatInterval) {
              case "days":
                nextDate.setDate(nextDate.getDate() + 1);
                break;
              case "weeks":
                nextDate.setDate(nextDate.getDate() + 7);
                break;
              case "months":
                nextDate.setMonth(nextDate.getMonth() + 1);
                if (nextDate.getDate() !== originalDay) {
                  nextDate.setDate(
                    Math.min(
                      originalDay,
                      new Date(
                        nextDate.getFullYear(),
                        nextDate.getMonth() + 1,
                        0
                      ).getDate()
                    )
                  );
                }
                break;
              case "quarters":
                nextDate.setMonth(nextDate.getMonth() + 3);
                // Handle month transitions properly for quarterly intervals
                if (nextDate.getDate() !== originalDay) {
                  nextDate.setDate(
                    Math.min(
                      originalDay,
                      new Date(
                        nextDate.getFullYear(),
                        nextDate.getMonth() + 1,
                        0
                      ).getDate()
                    )
                  );
                }
                break;
            }
            startDate = new Date(nextDate);
          }

          // Always include the repeatUntil date if not already present
          const formattedRepeatUntil = formatDate(repeatUntilDate);
          if (
            dates.length > 0 &&
            formatDate(dates[dates.length - 1]) !== formattedRepeatUntil
          ) {
            dates.push(new Date(repeatUntilDate));
          }

          // Batch create milestone objects
          const expandedInstances = dates.map((date) => ({
            ...milestone,
            deadline: formatDate(new Date(date)),
            repeat: false,
            repeatInterval: undefined,
            repeatUntil: undefined, // Clear repeatUntil for expanded milestones
            status: milestone.status || false,
          }));

          return [...acc, ...expandedInstances];
        }

        // For existing milestones or non-repeated ones
        return [
          ...acc,
          {
            ...milestone,
            deadline: milestone.deadline,
            status: milestone.status || false,
          },
        ];
      }, []);

      // console.log("Processed milestones:", processedMilestones);

      const formData = new FormData();
      formData.set("goal_title", data.goal_title);
      formData.set("goal_year", data.goal_year);
      formData.set("goal_category", data.goal_category);
      formData.append("milestones", JSON.stringify(processedMilestones));
      formData.append("favourite", selectedGoal.favourite);

      if (data.goal_img?.[0]) {
        formData.append("goal_img", data.goal_img[0]);
      } else if (data.goal_img instanceof File) {
        formData.append("goal_img", data.goal_img);
      }

      const result = await updateGoal(formData, selectedGoal.id);

      // console.log("Server response:", result);

      // Batch update state
      const updatedGoal = {
        ...result.data.goal,
        year: Number(result.data.goal.year),
      };

      const updatedMilestones = result.data.milestones.map((m) => ({
        ...m,
        status: m.status ?? false,
      }));

      // Single state update
      setUserData((prevData) => ({
        ...prevData,
        goals: prevData.goals.map((g) =>
          g.id === updatedGoal.id ? updatedGoal : g
        ),
        milestones: [
          ...prevData.milestones.filter((m) => m.goal_id !== selectedGoal.id),
          ...updatedMilestones,
        ],
      }));

      showToast("Goal was successfully updated", "success");
      setSidebarEditToggled(false);
    } catch (err) {
      console.error("Error updating goal:", err);
      showToast("Failed to update goal. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };



  const handleCategoryInputChange = (e) => {
    let inputValue = e.target.value;
    if (inputValue.length > 0) {
      inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
    }
    setValue("goal_category", inputValue);
    const filtered = categories.filter((cat) =>
      cat.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredCategories(filtered);
    setCategoryDropdown(filtered.length > 0);
  };

  const handleAddMilestone = () => {
    const currentGoalYear = watch("goal_year");
    const defaultRepeatUntil = currentGoalYear
      ? formatDate(new Date(currentGoalYear, 11, 31))
      : formatDate(new Date(new Date().getFullYear(), 11, 31)); // Default to end of current year if goal_year is not set

    insert(watchedMilestones.length, {
      title: "",
      deadline: "",
      repeat: false,
      repeatInterval: "",
      repeatUntil: defaultRepeatUntil,
      status: false,
    });
    newMilestoneFlags.current.push(true);
  };

  const handleRemoveMilestone = (index) => {
    remove(index);
    newMilestoneFlags.current.splice(index, 1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setCategoryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper for category capitalization
  const capitalizeCategory = (category) => {
    return category
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Helper for milestone field changes
  const handleMilestoneFieldChange = (index, field, value) => {
    const updated = [...watchedMilestones];
    updated[index][field] = value;
    // If enabling repeat and repeatUntil is empty, set to last day of year
    if (field === "repeat" && value === true) {
      const year = watch("goal_year") || new Date().getFullYear();
      if (!updated[index].repeatUntil) {
        updated[index].repeatUntil = formatDate(new Date(year, 11, 31));
      }
    }
    setValue("milestones", updated);
  };

  // Render milestone group
  const renderMilestoneGroup = (title, group) => {
    const groupKey = group[0].id || group[0].originalIndex; // Use stable key
    return (
      <div key={groupKey} className="relative">
        <div
          className={`relative flex flex-col ${!expandedGroups[title] && group.length > 1 ? "" : ""}`}
        >
          {group.map((field, index) => {
            const isNew = newMilestoneFlags.current[field.originalIndex];
            const isFirst = index === 0;
            const isStackedAndVisible =
              !expandedGroups[title] && index > 0 && index < 3;
            const displayFull = expandedGroups[title] || isFirst;

            if (!displayFull && !isStackedAndVisible) return null;

            return (
              <div
                key={field.id || field.originalIndex}
                className={`
                    bg-white border card-base-border rounded-md p-4
                    ${isFirst || expandedGroups[title] ? "relative" : "absolute w-full"}
                    ${isStackedAndVisible ? "shadow-md" : ""}
                `}
                style={
                  isStackedAndVisible
                    ? {
                        top: `${index * (isMobile ? 5 : 8)}px`,
                        left: `${index * (isMobile ? 5 : 8)}px`,
                        zIndex: group.length - index,
                      }
                    : {
                        zIndex: group.length - index,
                        marginBottom:
                          expandedGroups[title] && index < group.length - 1
                            ? "12px"
                            : "0",
                      }
                }
              >
                <div className="max-w-[90%] flex flex-col gap-5">
                  <InputText
                    label={copy.addgoal.form.label_milestone_title}
                    {...register(`milestones.${field.originalIndex}.title`)}
                    error={
                      errors.milestones?.[field.originalIndex]?.title?.message
                    }
                    details={`${watch(`milestones.${field.originalIndex}.title`)?.length || 0}/100 characters`}
                  />
                  <InputDate
                    label="Set deadline"
                    {...register(`milestones.${field.originalIndex}.deadline`)}
                    error={
                      errors.milestones?.[field.originalIndex]?.deadline
                        ?.message
                    }
                    details={`${
                      watch(`milestones.${field.originalIndex}.deadline`)
                        ?.length || 0
                    }/10 characters`}
                  />
                  {isNew && (
                    <div
                      className={`${isMobile ? "flex flex-col" : "flex flex-row"} gap-2`}
                    >
                      <div className="flex gap-2 items-center">
                        <InputCheckbox
                          label="Repeat"
                          {...register(
                            `milestones.${field.originalIndex}.repeat`
                          )}
                        />
                        {watchedMilestones?.[field.originalIndex]?.repeat && (
                          <InputSelect
                            {...register(
                              `milestones.${field.originalIndex}.repeatInterval`
                            )}
                            options={[
                              {
                                label:
                                  copy.addgoal.form.label_milestone_repeat_1,
                                value: "days",
                              },
                              {
                                label:
                                  copy.addgoal.form.label_milestone_repeat_2,
                                value: "weeks",
                              },
                              {
                                label:
                                  copy.addgoal.form.label_milestone_repeat_3,
                                value: "months",
                              },
                              {
                                label:
                                  copy.addgoal.form.label_milestone_repeat_4,
                                value: "quarters",
                              },
                            ]}
                            error={
                              errors.milestones?.[field.originalIndex]
                                ?.repeatInterval?.message
                            }
                          />
                        )}
                      </div>
                      <div
                        className={`flex gap-2 items-center ${isMobile ? "!ml-8" : ""}`}
                      >
                        {watchedMilestones?.[field.originalIndex]?.repeat && (
                          <>
                            <p>until</p>
                            <InputDate
                              value={formatDate(
                                watchedMilestones?.[field.originalIndex]
                                  ?.repeatUntil
                              )}
                              onChange={(e) =>
                                handleMilestoneFieldChange(
                                  field.originalIndex,
                                  "repeatUntil",
                                  e.target.value
                                )
                              }
                              error={
                                errors.milestones?.[field.originalIndex]
                                  ?.repeatUntil?.message
                              }
                              details={`${watchedMilestones?.[field.originalIndex]?.repeatUntil ? formatDate(watchedMilestones?.[field.originalIndex]?.repeatUntil).length : 0}/10 characters`}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="absolute top-2 right-2">
                  <IconCircle
                    icon={IoCloseOutline}
                    onClick={() => handleRemoveMilestone(field.originalIndex)}
                    disabled={milestones.length === 1}
                  />
                </div>
              </div>
            );
          })}
          {group.length > 1 && !expandedGroups[title] && (
            <div
              className={`mt-auto text-center relative z-[99999] ${isMobile ? "!-mt-2" : ""}`}
            >
              {isMobile ? (
                <BtnSecondaryS
                  buttonCTA={`${copy.dropdown.view_all} (${group.length})`}
                  type="button"
                  onClickLink={() =>
                    setExpandedGroups((prev) => ({
                      ...prev,
                      [title]: true,
                    }))
                  }
                />
              ) : (
                <BtnSecondaryM
                  buttonCTA={`${copy.dropdown.view_all} (${group.length})`}
                  type="button"
                  onClickLink={() =>
                    setExpandedGroups((prev) => ({
                      ...prev,
                      [title]: true,
                    }))
                  }
                />
              )}
            </div>
          )}
          {group.length > 1 && expandedGroups[title] && (
            <div className="mt-5 text-right mb-5">
              <span
                className="text-primary text-sm font-semibold cursor-pointer"
                onClick={() =>
                  setExpandedGroups((prev) => ({
                    ...prev,
                    [title]: false,
                  }))
                }
              >
                {copy.dropdown.show_less || "Show less"}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Main content
  const mainContent = (
    <div className="flex flex-col gap-8">
      <Headline
        headline="Edit goal"
        description="Break down your goal into practical tasks"
      />
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8">
        <div className="flex flex-col gap-4 w-full">
          <InputText
            label={copy.addgoal.form.label_title}
            placeholder={copy.addgoal.form.placeholder_goal_title}
            {...register("goal_title")}
            error={errors.goal_title?.message}
            details={copy.addgoal.form.details_goal_title.replace(
              "{count}",
              watch("goal_title")?.length || 0
            )}
          />
          <InputFile
            label={copy.addgoal.form.label_img}
            {...register("goal_img")}
            onChange={(e) => {
              setValue("goal_img", e.target.files);
            }}
            error={errors.goal_img?.message}
          />

          <InputSelect
            label={copy.addgoal.form.label_deadline}
            id="year"
            value={watch("goal_year") || ""}
            onChange={(e) => setValue("goal_year", Number(e.target.value))}
            error={errors.goal_year?.message}
            options={filteredYears
              .sort((a, b) => a - b)
              .map(year => ({
                label: year.toString(),
                value: year.toString()
              }))}
          />

          <div className="relative" ref={categoryDropdownRef}>
            <InputText
              label={copy.addgoal.form.label_category}
              {...register("goal_category")}
              value={capitalizeCategory(watch("goal_category") || "")}
              onChange={handleCategoryInputChange}
              onFocus={() => setCategoryDropdown(true)}
              error={errors.goal_category?.message}
              details={copy.addgoal.form.details_goal_category.replace(
                "{count}",
                watch("goal_category")?.length || 0
              )}
            />
            {categoryDropdown && filteredCategories.length > 0 && (
              <ul className="absolute bg-white card-base-border rounded-md shadow-lg z-[99999] mt-1 w-full">
                {filteredCategories.map((category, idx) => (
                  <DropdownInput
                    key={idx}
                    value={capitalizeCategory(category)}
                    onSelect={(val) => {
                      setValue("goal_category", capitalizeCategory(val));
                      setCategoryDropdown(false);
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <Subheadline
              subheadline={copy.addgoal.milestone_title}
              description={copy.addgoal.milesone_description}
            />
          </div>

          {Object.entries(
            milestones.reduce((acc, milestone, index) => {
              const watchedMilestone = watchedMilestones[index] || {};
              const title = watchedMilestone.title || "Untitled Milestone";
              if (!acc[title]) {
                acc[title] = [];
              }
              acc[title].push({
                ...milestone,
                ...watchedMilestone,
                originalIndex: index,
              });
              return acc;
            }, {})
          ).map(([title, group]) => renderMilestoneGroup(title, group))}
          <div className="flex justify-end">
            <BtnSecondaryM
              buttonCTA={
                isMobile ? <FiPlus size={20} /> : copy.addgoal.button_milestone
              }
              type="button"
              onClickLink={handleAddMilestone}
              className={
                isMobile
                  ? "w-10 h-10 rounded-full flex items-center justify-center p-0"
                  : ""
              }
            />
          </div>
        </div>

        <div className="flex self-center mt-10">
          <BtnPrimaryL
            buttonCTA={
              isSubmitting
                ? copy.addgoal.button_updating_goal || copy.dropdown.update
                : copy.addgoal.button_update_goal || copy.dropdown.update
            }
            type="submit"
            disabled={isSubmitting}
          />
        </div>
      </form>
    </div>
  );

  // Mobile layout
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-end md:hidden bg-black/30">
        <div className="relative w-full bg-white rounded-t-xl shadow-2xl p-4 pb-8 card-base-border animate-slide-up max-h-[90vh] overflow-y-auto">
          {/* X button */}
          <div className="flex justify-end z-20 mb-2">
            <IconCircle
              icon={IoCloseOutline}
              onClick={() => setSidebarEditToggled(false)}
              ariaLabel="Close"
            />
          </div>
          <div className="flex flex-col gap-8 mt-4 !mb-15">{mainContent}</div>
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="fixed top-0 right-0 h-screen w-1/2 overflow-y-scroll z-50 px-6 sm:px-12 md:px-16 lg:px-24 xl:px-30 py-6 sm:py-8 md:py-10 lg:py-12 bg-white shadow-lg card-base-border-left">
      <div className="gap-2 absolute top-2 left-8">
        <IconCircle
          icon={HiOutlineChevronDoubleRight}
          onClick={() => setSidebarEditToggled(false)}
        />
      </div>

      <div className="flex flex-col gap-8 !mb-10">{mainContent}</div>
    </div>
  );
}

NavSidebarEdit.propTypes = {
  setSidebarEditToggled: PropTypes.func.isRequired,
  selectedGoal: PropTypes.object.isRequired,
};
